import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";

// Emoji flags come from country ISO-2 codes.
function isoToFlag(iso2) {
  if (!iso2 || iso2.length !== 2) return "🏳";
  const A = 0x1F1E6;
  return String.fromCodePoint(A + iso2.toUpperCase().charCodeAt(0) - 65, A + iso2.toUpperCase().charCodeAt(1) - 65);
}

const DEFAULT = {
  title: "Atuação Internacional",
  subtitle: "Projetos além das fronteiras do Brasil.",
  countries: [
    { name: "Portugal", iso: "PT", projects: 3 },
    { name: "Argentina", iso: "AR", projects: 2 },
    { name: "Paraguai", iso: "PY", projects: 1 },
    { name: "Uruguai", iso: "UY", projects: 1 },
  ],
};

export default function InternationalPresenceEditor() {
  const [cfg, setCfg] = useState(DEFAULT);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/content/kv/international_presence").then(({ data }) => {
      if (data) setCfg({ ...DEFAULT, ...data });
    }).catch(() => {});
  }, []);

  const upd = (i, patch) => setCfg((s) => ({ ...s, countries: s.countries.map((c, j) => j === i ? { ...c, ...patch } : c) }));
  const add = () => setCfg((s) => ({ ...s, countries: [...(s.countries || []), { name: "Novo País", iso: "US", projects: 1 }] }));
  const remove = (i) => setCfg((s) => ({ ...s, countries: s.countries.filter((_, j) => j !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/content/kv/international_presence", { value: cfg });
      toast.success("Atuação internacional salva.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl">Atuação Internacional</h1>
          <p className="text-sm text-neutral-500 mt-1 max-w-2xl">Países onde a agência atua. Use o código ISO 2-letras (BR, PT, AR, US…) — a bandeira é gerada automaticamente.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={add} className="text-xs bg-black text-white px-3 py-2 rounded-full hover:bg-accent-orange"><Plus className="w-3 h-3 inline mr-1" /> Novo país</button>
          <button onClick={save} disabled={busy} data-testid="intl-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" /> Salvar</button>
        </div>
      </div>

      <section className="border border-black/10 rounded-md p-5 mb-6 grid md:grid-cols-2 gap-3">
        <div><label className="text-xs text-neutral-500">Título</label><input value={cfg.title} onChange={(e) => setCfg({ ...cfg, title: e.target.value })} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>
        <div><label className="text-xs text-neutral-500">Subtítulo</label><input value={cfg.subtitle} onChange={(e) => setCfg({ ...cfg, subtitle: e.target.value })} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>
      </section>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {(cfg.countries || []).map((c, i) => (
          <div key={i} className="border border-black/10 rounded-md p-4">
            <div className="flex justify-between items-start mb-3">
              <div className="text-4xl leading-none">{isoToFlag(c.iso)}</div>
              <button onClick={() => remove(i)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2">
              <input value={c.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="País" className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
              <input value={c.iso} onChange={(e) => upd(i, { iso: e.target.value.toUpperCase().slice(0, 2) })} placeholder="ISO 2 (ex: PT)" className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono uppercase" maxLength={2} data-testid={`intl-iso-${i}`} />
              <input type="number" min={0} value={c.projects ?? 0} onChange={(e) => upd(i, { projects: Number(e.target.value) })} placeholder="Nº total de projetos" className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
              <div>
                <label className="text-[11px] text-neutral-500">Produtos trabalhados (1 por linha)</label>
                <textarea
                  value={(c.products || []).join("\n")}
                  onChange={(e) => upd(i, { products: e.target.value.split("\n") })}
                  rows={4}
                  placeholder="Ex:&#10;Vale do Lima Residências&#10;Porto Living&#10;Ericeira Vista"
                  className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono text-xs"
                  data-testid={`intl-products-${i}`}
                />
                <div className="text-[10px] text-neutral-400 mt-1">Aparece ao expandir a caixinha do país (site público).</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
